import { useConfigStore, type languageType } from '@/stores';
import { defaultConfig } from '@/stores/configStore';
import { AppUtils } from '@/utils/app';
import { useTranslation } from 'react-i18next';
import { useCallback, useRef } from 'react';

export const useAccessibilityTTS = () => {
    const { t } = useTranslation();
    const synthRef = useRef<SpeechSynthesis | null>(typeof window !== 'undefined' ? window.speechSynthesis : null);
    const lastFocusedGroupRef = useRef<Element | null>(null);
    const language = useConfigStore((state) => state.config?.ui.language);
    const volume = useConfigStore((state) => state.config?.ui.volume);
    const pendingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const latestRequestIdRef = useRef(0);
    const voice = useConfigStore((state) => state?.config?.tts?.voice);

    // TTS functionality
    const getSelectedValue = useCallback((name: string) => {
        const element = document.querySelector(`button[role="radio"][name="${name}"][aria-checked="true"]`) as HTMLButtonElement;
        return element?.value || (name === 'rate' ? '1.5' : '1');
    }, []);

    const speak = useCallback(
        (text: string, lang?: languageType, initialVolume?: number, forceSpeak?: boolean) => {
            if (!voice && !forceSpeak) {
                return;
            }

            const synth = synthRef.current;
            if (!synth) return;

            // Cancel any ongoing speech immediately
            if (synth.speaking) {
                synth.cancel();
            }

            // Clear any previously scheduled speak to ensure last call wins
            if (pendingTimeoutRef.current) {
                clearTimeout(pendingTimeoutRef.current);
                pendingTimeoutRef.current = null;
            }

            if (text) {
                const requestId = ++latestRequestIdRef.current;
                const timeoutId = setTimeout(() => {
                    // Only proceed if this is still the latest scheduled request
                    if (requestId !== latestRequestIdRef.current) return;
                    const currentSynth = synthRef.current;
                    if (!currentSynth) return;

                    const utterance = new SpeechSynthesisUtterance(text);
                    utterance.lang = AppUtils.getLanguageTTS(lang || language) || 'ko-KR';
                    try {
                        utterance.pitch = parseFloat(getSelectedValue('pitch'));
                        utterance.rate = parseFloat(getSelectedValue('rate'));
                    } catch {
                        utterance.pitch = 1;
                        utterance.rate = 1.5;
                    }

                    const currentVolume = useConfigStore.getState()?.config?.ui?.volume;
                    utterance.volume = (currentVolume * 2) / 10;

                    utterance.onerror = () => { };
                    console.log('text: ', text);
                    currentSynth.speak(utterance);
                }, 100);

                pendingTimeoutRef.current = timeoutId;

                // Return cleanup function
                return () => {
                    if (pendingTimeoutRef.current === timeoutId) {
                        clearTimeout(timeoutId);
                        pendingTimeoutRef.current = null;
                    }
                };
            }
        },
        [getSelectedValue, volume, language, voice]
    );
    const speakSequentially = useCallback(
        (text: string, lang?: languageType, initialVolume?: number) => {
            if (!voice) {
                return;
            }

            const synth = synthRef.current;

            if (!synth) return;

            // Cancel any ongoing speech immediately
            if (synth.speaking) {
                synth.cancel();
            }

            if (text) {
                const timeoutId = setTimeout(() => {
                    const currentSynth = synthRef.current;
                    if (!currentSynth) return;

                    const utterance = new SpeechSynthesisUtterance(text);
                    utterance.lang = AppUtils.getLanguageTTS(lang || language) || 'ko-KR';
                    try {
                        utterance.pitch = parseFloat(getSelectedValue('pitch'));
                        utterance.rate = parseFloat(getSelectedValue('rate'));
                    } catch {
                        utterance.pitch = 1;
                        utterance.rate = 1.5;
                    }
                    utterance.volume = initialVolume ? initialVolume / defaultConfig.ui.volume : volume / defaultConfig.ui.volume || 1;

                    utterance.onerror = () => { };
                    // eslint-disable-next-line no-console
                    console.log('speak sequentially text: ', text);
                    currentSynth.speak(utterance);
                }, 100);

                pendingTimeoutRef.current = timeoutId;

                // Return cleanup function
                return () => {
                    if (pendingTimeoutRef.current === timeoutId) {
                        clearTimeout(timeoutId);
                        pendingTimeoutRef.current = null;
                    }
                };
            }
        },
        [getSelectedValue, volume, language, voice]
    );

    // Accessibility announcement functionality
    const getTextToSpeak = useCallback(
        (element: Element | null) => {
            if (!element) return '';
            if (element.tagName === 'BODY') {
                return element.getAttribute('aria-label');
            }
            const elementInfo = [];
            const currentGroup = element.closest('[role="radiogroup"], [role="list"], [role="tablist"], [data-region-nav]');

            const role = element.getAttribute('role');

            if (element.matches('button[role="radio"][name="pitch"], button[role="radio"][name="rate"]'), element.closest('[role="tablist"]')) {
                const radioGroup = element.closest('[role="radiogroup"]');
                const groupLabel = radioGroup ? radioGroup.getAttribute('aria-label') : '';
                const isChecked = element.getAttribute('aria-checked') === 'true';

                const textLabel = element.getAttribute('aria-label') || element.textContent.trim();
                elementInfo.push(
                    isChecked ? t('common.button.tts.status_selected') : t('common.button.tts.status_not_selected'),
                    groupLabel,
                    textLabel
                );
            } else {
                if (role === 'switch' || role === 'radio' || role === 'tab') {
                    const isChecked = element.getAttribute('aria-checked') === 'true' || element.getAttribute('aria-selected') === 'true';
                    elementInfo.push(isChecked ? t('common.button.tts.status_selected') : t('common.button.tts.status_not_selected'));
                } else if (role === 'button' && element.hasAttribute('aria-pressed')) {
                    const isPressed = element.getAttribute('aria-pressed') === 'true';
                    elementInfo.push(isPressed ? t('common.button.tts.status_selected') : t('common.button.tts.status_not_selected'));
                } else if (element instanceof HTMLInputElement && element.type === 'checkbox') {
                    elementInfo.push(element.checked ? t('common.button.tts.status_selected') : t('common.button.tts.status_not_selected'));
                }
                if (element.hasAttribute('aria-valuetext')) {
                    elementInfo.push(element.getAttribute('aria-valuetext'));
                } else {
                    const value =
                        element.getAttribute('aria-label') ||
                        (element instanceof HTMLInputElement && element.labels && element.labels[0] ? element.labels[0].textContent.trim() : null) ||
                        element.textContent.trim() ||
                        (element instanceof HTMLImageElement ? element.alt : undefined);
                    if (value) elementInfo.push(value);
                }
            }

            lastFocusedGroupRef.current = currentGroup;
            // NOTE: get data-role text by dataset.role
            let roleText = (element as HTMLElement).dataset?.role;
            // NOTE: if roleText is not found, set roleText based on element type
            if (!roleText) {
                if (element instanceof HTMLInputElement && element.type === 'checkbox') roleText = t('common.button.tts.checkbox');
                else if (role === 'radio') roleText = t('common.button.tts.radio_button');
                else if (role === 'button') roleText = t('common.button.tts.button');
                else if (role === 'switch') roleText = t('common.button.tts.switch_button');
                else if (role === 'tab') roleText = t('common.button.tts.tab_button');
                // popup button
                else if (role === 'dialog') roleText = t('common.button.tts.popup_button');
            }
            if (roleText) elementInfo.push(roleText);
            const desc = (element as HTMLElement).dataset?.desc || element.getAttribute('aria-description');
            if (desc) elementInfo.push(desc);
            const elementText = elementInfo.join(', ');
            return elementText;
        },
        [t]
    );

    const speakAnnouncement = useCallback(
        (text: string, lang?: languageType, initialVolume?: number) => {
            return speak(text, lang, initialVolume, true);
        },
        [speak]
    );

    // Function to speak only aria-label
    const speakAriaLabel = useCallback(
        (element: Element | null) => {
            if (!element) return;
            const ariaLabel = element.getAttribute('aria-label');
            if (ariaLabel) {
                const cleanup = speak(ariaLabel);
                return cleanup;
            }
        },
        [speak]
    );

    // Combined function to speak element text
    const speakElement = useCallback(
        (element: Element | null) => {
            if (!element) return;
            // Call speak and get potential cleanup function
            const cleanup = speak(getTextToSpeak(element) || '');

            return cleanup;
        },
        [speak, getTextToSpeak]
    );

    return {
        speak,
        speakAnnouncement,
        getTextToSpeak,
        speakElement,
        speakAriaLabel,
        speakSequentially,
    };
};

export default useAccessibilityTTS;
